import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getFullApiUrl, podcastId } from "../../../config";
import {
  extractVol,
  formatDateSlash,
  getPodcastSession,
  renderCustomContent,
} from "../../../utils";
import { Badge, Icon, PaywallCard } from "../../../designSystem";
import { useMembership } from "../../../hooks/useMembership";
import Notice from "../../ui/Notice";
import VideoPlayer from "./VideoPlayer";
import { LoadingPage } from "../../LoadingPage";
import { ErrorPage } from "../../ErrorPage";

export const VideoPage = () => {
  const { id: videoId } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { isMember } = useMembership();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const podcastSession = getPodcastSession();

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          getFullApiUrl("/api/v1/subscribe/get_video_with_video_id"),
          {
            params: { podcast: podcastId, video: videoId },
          }
        );
        setVideo(response.data.video);
      } catch (err) {
        console.error("Error fetching video data:", err);
        setError("视频内容加载失败，请稍后重试。");
      } finally {
        setLoading(false);
      }
    };

    if (videoId) {
      fetchVideo();
    }
  }, [videoId]);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (!video) {
    return <ErrorPage error="视频不存在" />;
  }

  const vol = extractVol(video.Title);
  const locked = !!video.IsPaid && !isMember;
  const offline =
    video.VideoDirectory === "32faa640-81ea-4b61-88ac-714c842d69f6" ||
    video.VideoDirectory === "7d5836cb-2181-4d4e-b489-d42d3a4af5a5";

  return (
    <div className="lt-main">
      <button className="lt-back" onClick={() => navigate("/videos")}>
        <Icon name="arrow_back" size={16} />
        返回视频
      </button>
      <div style={{ margin: "20px 0 24px" }}>
        {vol != null ? (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--text-sm)",
              color: "var(--accent-strong)",
              fontWeight: 600,
            }}
          >
            Vol.{vol}
          </span>
        ) : null}
        <h1 style={{ fontSize: "var(--text-2xl)", margin: "6px 0 10px" }}>
          {video.Title}
        </h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            fontSize: "var(--text-sm)",
            color: "var(--text-3)",
            fontFamily: "var(--font-mono)",
          }}
        >
          <span>{formatDateSlash(video.PublishedAt)}</span>·
          {video.IsPaid ? <Badge variant="member" /> : null}
          <Badge variant="video" />
        </div>
      </div>

      {offline ? (
        <Notice variant="warning" style={{ marginBottom: 16, maxWidth: 820 }}>
          由于水印未去除，视频先暂时下架，处理后会重新上架。
        </Notice>
      ) : null}

      {locked ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "28px 0 40px" }}>
          <PaywallCard
            title="这条视频是会员专享"
            desc="成为年度会员，解锁全部单集和视频版。"
            onJoin={() => navigate("/membership-intro")}
            onLogin={isAuthenticated ? undefined : () => navigate("/login")}
          />
        </div>
      ) : (
        <div className="lt-videoshell" style={{ maxWidth: 820 }}>
          <VideoPlayer
            podcastId={video.PodcastID}
            videoDirectory={video.VideoDirectory}
            podcastSession={podcastSession}
          />
        </div>
      )}

      <div className="lt-prose" style={{ marginTop: 28 }}>
        {renderCustomContent(video.Content)}
      </div>
    </div>
  );
};
